import { Trans, useTranslation } from 'next-i18next'
import {
    Container,
    Content,
    LastUpdateContainer,
    ListItem,
    OrderedList,
    OrderedListItem,
    PageHeaderContainer,
    PageTitle,
    Section,
    SubSectionTitle,
} from '@/features/LegalPages/styles/textElements.styles'
import {
    COMPONENTS,
    OUR_WEBSITE_LINK,
    SUPPORT_EMAIL,
} from '@/features/LegalPages/Constant/constants'
import { ReactNode } from 'react'

type SummaryLinkProps = {
    children?: ReactNode
}

interface LinkProps {
    children?: ReactNode
    href?: string
}

interface TermsAndConditionsProps {
    isIOS?: boolean
}

const LinkComponent = ({ children, href = '' }: LinkProps) => {
    if (
        (children && typeof children[0] === 'string' && children[0] === OUR_WEBSITE_LINK) ||
        href === OUR_WEBSITE_LINK
    ) {
        return (
            <a className="color-primary" target="_blank" rel="noreferrer" href={OUR_WEBSITE_LINK}>
                {children[0].replace(/https?:\/\//, '')}
            </a>
        )
    }
    return (
        <a className="color-primary" href={href}>
            {children}
        </a>
    )
}

const SummaryLink = ({ children }: SummaryLinkProps) => {
    const id = children[0].replace(/\s/g, '-').replace(/\./g, '').toLowerCase()
    return (
        <ListItem className="summary-link">
            <SubSectionTitle>
                <LinkComponent href={`#${id}`}>{children}</LinkComponent>
            </SubSectionTitle>
        </ListItem>
    )
}

const TermsAndConditions = ({ isIOS = false }: TermsAndConditionsProps) => {
    const namespace = isIOS ? 'termsAndConditions-ios' : 'termsAndConditions'
    const { t } = useTranslation(['termsAndConditions', 'termsAndConditions-ios'])

    const hasEnglishPrevailWarning =
        t('englishPrevailWarning', {
            ns: namespace,
            defaultValue: null,
        }) !== null

    const WarningComponent = hasEnglishPrevailWarning && (
        <Trans
            t={t}
            style={{ fontWeight: 'bold', textDecoration: 'underline' }}
            i18nKey="englishPrevailWarning"
            ns={namespace}
        >
            <></>
        </Trans>
    )

    return (
        <Container>
            <Content>
                <PageHeaderContainer>
                    <PageTitle className="transform">{t('title', { ns: namespace })}</PageTitle>
                    <LastUpdateContainer>
                        <Trans t={t} i18nKey="lastUpdated" ns={namespace}>
                            <></>
                        </Trans>
                    </LastUpdateContainer>
                </PageHeaderContainer>
                {WarningComponent}
                <Section>
                    <Trans
                        t={t}
                        i18nKey="preface"
                        ns={namespace}
                        components={{
                            ...COMPONENTS,
                            linkItem: <LinkComponent href={OUR_WEBSITE_LINK} />,
                        }}
                    />
                </Section>
                <Section>
                    <Trans
                        t={t}
                        i18nKey="contents"
                        ns={namespace}
                        components={{
                            ...COMPONENTS,
                            orderedListItem: <SummaryLink />,
                        }}
                    />
                </Section>
                <OrderedList>
                    <OrderedListItem id="definitions">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="definitions"
                                ns={namespace}
                                values={{ OUR_WEBSITE_LINK }}
                                components={{
                                    ...COMPONENTS,
                                    linkItem: <LinkComponent href="/legal/privacy" />,
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="conditions-of-use">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="conditionsOfUse"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="eligibility">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="eligibility"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    orderedList: <OrderedList className="nested-ordered-list" />,
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="services">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="services"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="user-identity-verification">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="userIdentityVerification"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    unorderedListItem: (
                                        <ListItem className="list-type-lower-latin" />
                                    ),
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="orders-execution">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="ordersExecution"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="price-and-fees">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="priceAndFees"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="modifications-of-the-terms">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="modificationsOfTheTerms"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="termination-of-the-agreement-closing-the-account">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="terminationOfTheAgreement"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    unorderedListItem: (
                                        <ListItem className="list-type-lower-latin" />
                                    ),
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="risk-factors">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="riskFactors"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    unorderedListItem: (
                                        <ListItem className="list-type-lower-latin" />
                                    ),
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="account-safety-requirements">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="accountSafetyRequirements"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    unorderedListItem: (
                                        <ListItem className="list-type-lower-latin" />
                                    ),
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="prohibited-activity">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="prohibitedActivity"
                                ns={namespace}
                                components={{ ...COMPONENTS }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="applicable-law-and-disputes">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="applicableLawAndDisputes"
                                ns={namespace}
                                values={{ SUPPORT_EMAIL }}
                                components={{
                                    ...COMPONENTS,
                                    linkItem: <LinkComponent href={`mailto:${SUPPORT_EMAIL}`} />,
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem id="miscellaneous">
                        <Section className="section-container">
                            <Trans
                                t={t}
                                i18nKey="miscellaneous"
                                ns={namespace}
                                components={{
                                    ...COMPONENTS,
                                    unorderedListItem: (
                                        <ListItem className="list-type-lower-latin" />
                                    ),
                                    orderedList: <OrderedList className="section-list" />,
                                }}
                            />
                        </Section>
                    </OrderedListItem>
                </OrderedList>
            </Content>
        </Container>
    )
}

export default TermsAndConditions
